import type { Language, Languages, PhraseKey } from "./types";
import { getQueryParam } from "../helpers/browser/queryParams";

import en_us from "./data/en-us.json";
import { getNestedStringValue } from "../helpers";

import {
  validateRequired,
  validateContainChars,
  validateMax,
  validateEmail,
  validateEmailStartPeriods,
  validateEmailEndPeriods,
  validateEmailDoublePeriods,
  validateZip
} from "wvus-forms";

import {
  validateExpirationDate,
  validateIsNotExpired,
  validateNotPast10Years,
  validatePhoneAreacode,
  validatePhone
} from "../helpers";

import es_mx from "./data/es-mx.json";
import ko from "./data/ko.json";
import zh_tw from "./data/zh-tw.json";
import qa_test from "./data/qa-test.json";

export const languages: Languages = {
  english: en_us, // English
  spanish: es_mx, // Spanish
  korean: ko, // Korean
  chinese: zh_tw, // Chinese
  qa: qa_test // QA Test
};

// Calculate the language in this order:
// 1. Get the language from the URL's lang query string parameter if it exists.
// 2. Get the language from window.wvusChosenEventData.Language if it exists.
// 3. Default to English.
let languageName = (
  getQueryParam("lang") ||
  window.wvusChosenEventData?.[0]?.Language.toString() ||
  "English"
).toLowerCase();

if (!languages[languageName]) {
  languageName = "english";
}

export const language: Language = languages[languageName];

export const getPhrase = (phrase: PhraseKey): string => getNestedStringValue(language, phrase);

/**
 * Wraps a given validator function to include a localized message.
 *
 * @param validator - A function that takes a string value and returns an object containing a boolean `valid` property and a `message` string.
 * @param phraseKey - A key used to retrieve the localized message.
 * @returns A new validator function that includes the localized message.
 */
export const wrapValidator = (
  validator: (value: string) => {
    valid: boolean;
    message: string;
  },
  phraseKey: PhraseKey,
  mutator: (value: string) => string = (value) => value
) => {
  return (value: string) => {
    const rawValue = getPhrase(phraseKey);
    const processedValue = mutator ? mutator(rawValue) : rawValue;
    return { ...validator(value), message: processedValue, messagePhraseKey: phraseKey };
  };
};

export const validators = {
  required: wrapValidator(validateRequired, "errors.field_required"),
  invalidChars: wrapValidator(validateContainChars, "errors.field_invalid_characters"),
  maxLength: (maxLength: number) =>
    wrapValidator(validateMax(maxLength), "errors.field_max_length", (value) =>
      value.replace("{{maxLength}}", `${maxLength}`)
    ),
  expirationDate: wrapValidator(
    validateExpirationDate,
    "errors.field_expiration_invalid",
    (value) =>
      value.replace("{{example}}", `01/${(new Date().getFullYear() + 3).toString().slice(2)}`)
  ),
  isNotExpired: wrapValidator(validateIsNotExpired, "errors.field_expiration_expired"),
  notPast10Years: wrapValidator(validateNotPast10Years, "errors.field_expiration_past_10_years"),
  phoneAreaCode: wrapValidator(validatePhoneAreacode, "errors.field_phone_area_code_invalid"),
  phoneNumber: wrapValidator(validatePhone, "errors.field_phone_invalid"),
  zip: wrapValidator(validateZip, "errors.field_zip_invalid"),
  email: wrapValidator(validateEmail, "errors.field_email_invalid"),
  emailStartPeriods: wrapValidator(validateEmailStartPeriods, "errors.field_email_invalid"),
  emailEndPeriods: wrapValidator(validateEmailEndPeriods, "errors.field_email_invalid"),
  emailDoublePeriods: wrapValidator(validateEmailDoublePeriods, "errors.field_email_invalid")
  /*
  validateEmail,
  validateEmailStartPeriods,
  validateEmailEndPeriods,
  validateEmailDoublePeriods
  */
};

export * from "./Say";

export * from "./types";
